"use client"

import Image from "next/image"
import { Link, useT } from "../lib/router"
import { brandingCapabilities, brandingExamples, brandingService, brandingStages } from "../content/branding"
import { Breadcrumbs, Button, CheckList, Container, Eyebrow, FAQList, Icon, Section, SectionHeading } from "../components/ui"
import styles from "./Branding.module.css"

const projectPath = "/start-project?service=branding"

function IdentityBoard() {
  const t = useT()
  return (
    <figure className="relative min-w-0">
      <div className="rounded-r24 border border-line bg-mist p-4 sm:p-6" dir="ltr">
        <div className="mb-5 flex items-center justify-between gap-3 text-xs font-medium text-blue" lang="en">
          <span>ORKEST / IDENTITY SYSTEM</span>
          <span>01 — 03</span>
        </div>
        <div className={`relative flex min-h-52 items-center justify-center overflow-hidden rounded-r16 bg-blue px-10 py-16 sm:min-h-64 ${styles.logoPanel}`}>
          <span className="absolute start-5 top-4 text-xs font-medium text-white" lang="en">01 / LOGOTYPE</span>
          <img src="/brand/orkest-white.svg" alt="ORKEST" width={330} height={48} className="relative h-auto w-full max-w-72" />
          <span className="absolute bottom-4 end-5 text-xs text-white" lang="en">Digital, in harmony.</span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="flex flex-col justify-between rounded-r16 border border-line bg-white p-4 sm:p-5">
            <p className="text-xs font-medium text-blue" lang="en">02 / COLOR</p>
            <div className="mt-7 flex h-12 overflow-hidden rounded-r8" aria-hidden="true">
              <span className="w-1/2 bg-blue" /><span className="w-1/4 bg-ink" /><span className="w-1/4 bg-ice" />
            </div>
            <p className="mt-3 text-xs text-slate" lang="en">Blue / Ink / Ice</p>
          </div>
          <div className="flex flex-col justify-between rounded-r16 border border-line bg-white p-4 sm:p-5">
            <p className="text-xs font-medium text-blue" lang="en">03 / TYPE</p>
            <div className="mt-4 flex flex-wrap items-baseline gap-x-4 text-3xl font-medium text-ink sm:text-4xl">
              <span lang="en">Aa</span><span lang="ar" dir="rtl">أب</span>
            </div>
            <p className="mt-3 text-xs text-slate" lang="en">IBM Plex Sans</p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-4 rounded-r16 bg-ice p-5 text-blue">
          <img src="/brand/orkest-mark-blue.svg" alt="" width={34} height={32} className="h-8 w-auto shrink-0" />
          <p className="text-sm font-medium" lang="ar" dir="rtl">كل جزء، في مكانه.</p>
        </div>
      </div>
      <figcaption className="mt-4 text-sm text-slate">
        {t({ ar: "عرض توضيحي لنظام بصري باستخدام هوية أوركست.", en: "An illustrative visual system using Orkest’s own identity." })}
      </figcaption>
    </figure>
  )
}

function PackagingLayout() {
  const t = useT()
  return (
    <figure className="rounded-r24 border border-line bg-white p-5 sm:p-8">
      <div className="flex items-center justify-between gap-4 border-b border-line pb-4">
        <p className="text-sm font-medium text-ink">{t({ ar: "من التصميم إلى العبوة", en: "From artwork to packaging" })}</p>
        <span className="text-xs font-medium text-blue" lang="en" dir="ltr">PACKAGING / LAYOUT</span>
      </div>
      <svg viewBox="0 0 480 340" width="480" height="340" className="my-5 h-auto w-full" aria-hidden="true" focusable="false">
        <g fill="var(--color-mist)" stroke="var(--color-blue)" strokeWidth="1.5" strokeLinejoin="round">
          <path d="M58 110V92H82V62L96 48H164L178 62V92H202V110H294V92H318V62L332 48H400L414 62V92H438V110L454 124V224L438 238V256H414V286L400 300H332L318 286V256H294V238H202V256H178V286L164 300H96L82 286V256H58V238L42 224V124Z" />
          <path d="M82 110h96v128H82Z" fill="var(--color-ice)" stroke="none" />
          <path d="M318 110h96v128h-96Z" fill="var(--color-blue)" stroke="none" />
        </g>
        <g fill="none" stroke="var(--color-blue)" strokeWidth="1" strokeDasharray="5 5">
          <path d="M58 110h380M58 238h380M82 92h96M318 92h96M82 256h96M318 256h96M58 110v128M82 110v128M178 110v128M202 110v128M294 110v128M318 110v128M414 110v128M438 110v128" />
        </g>
        <image href="/brand/orkest-blue.svg" x="94" y="154" width="72" height="12" />
        <image href="/brand/orkest-white.svg" x="330" y="154" width="72" height="12" />
        <g fill="var(--color-blue)">
          <rect x="106" y="180" width="48" height="3" rx="1.5" />
          <rect x="114" y="190" width="32" height="3" rx="1.5" />
        </g>
        <g fill="white">
          <rect x="342" y="180" width="48" height="3" rx="1.5" />
          <rect x="350" y="190" width="32" height="3" rx="1.5" />
        </g>
      </svg>
      <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-ink">
        <span className="inline-flex items-center gap-2"><span className="w-6 border-t-2 border-blue" aria-hidden="true" />{t({ ar: "خط القص", en: "Cut line" })}</span>
        <span className="inline-flex items-center gap-2"><span className="w-6 border-t-2 border-dashed border-blue" aria-hidden="true" />{t({ ar: "خط الطي", en: "Fold line" })}</span>
      </div>
      <figcaption className="mt-5 border-t border-line pt-4 text-sm leading-6 text-slate">
        {t({ ar: "مخطط توضيحي. تُضبط أبعاد ومواصفات كل عبوة مع المورد قبل تجهيز ملفاتها للطباعة.", en: "Illustrative layout. Each pack’s dimensions and specifications are confirmed with the supplier before print files are prepared." })}
      </figcaption>
    </figure>
  )
}

function SelectedBrandingWork() {
  const t = useT()
  const examples = brandingExamples.filter((example) => example.published)
  if (examples.length === 0) return null

  return (
    <Section id="branding-work">
      <SectionHeading eyebrow={t({ ar: "أعمال مختارة", en: "Selected work" })} title={t({ ar: "هويات تحوّلت إلى واقع.", en: "Identities brought to life." })} />
      <div className="mt-12 grid gap-8 md:grid-cols-2">
        {examples.map((example) => (
          <article key={example.id} className="overflow-hidden rounded-r16 border border-line">
            <Image src={example.cover.src} alt={t(example.cover.alt)} width={example.cover.width} height={example.cover.height} sizes="(min-width: 1280px) 560px, (min-width: 768px) 50vw, 100vw" className="aspect-[4/3] w-full bg-mist object-cover" />
            <div className="p-6">
              <p className="text-sm font-medium text-blue">{t(example.category)}</p>
              <h3 className="mt-2 text-2xl font-medium text-ink">{t(example.title)}</h3>
              <p className="mt-3 text-base leading-7 text-slate">{t(example.summary)}</p>
              {example.caseStudyPath ? <Link to={example.caseStudyPath} className="mt-5 inline-flex min-h-11 items-center gap-2 font-medium text-blue underline underline-offset-4">{t({ ar: "استعرض المشروع", en: "Explore the project" })}<Icon name="arrow" flip className="size-5" /></Link> : null}
            </div>
          </article>
        ))}
      </div>
    </Section>
  )
}

export default function Branding() {
  const t = useT()
  return (
    <>
      <header className="border-b border-line bg-white">
        <Container className="pb-16 pt-10 md:pb-20">
          <Breadcrumbs items={[{ label: t({ ar: "الخدمات", en: "Services" }), to: "/services" }, { label: t(brandingService.title) }]} />
          <div className="mt-12 grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
            <div className="min-w-0">
              <Eyebrow>{t({ ar: "بناء العلامة والهوية", en: "Branding & visual identity" })}</Eyebrow>
              <h1 className="brand-h1 mt-6 font-medium text-ink">
                {t({ ar: "هوية تعبّر عنك.", en: "An identity that is yours." })}<br />
                <span className="text-blue">{t({ ar: "في كل نقطة تواصل.", en: "At every touchpoint." })}</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-slate">{t(brandingService.heroLead)}</p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button to={projectPath}>{t({ ar: "لنبدأ بناء هويتك", en: "Let’s build your brand" })}</Button>
                <a href="#branding-process" className="inline-flex min-h-12 items-center gap-2 rounded-r8 px-3 text-base font-medium text-blue hover:bg-ice">
                  {t({ ar: "اكتشف مراحل العمل", en: "Explore our process" })}<span aria-hidden="true">↓</span>
                </a>
              </div>
              <p className="mt-7 text-sm text-slate">{t({ ar: "تأسيس علامة جديدة · تطوير هوية قائمة · إطلاق منتج", en: "New brands · Identity refreshes · Product launches" })}</p>
            </div>
            <IdentityBoard />
          </div>
        </Container>
      </header>

      <nav aria-label={t({ ar: "أقسام خدمة الهوية", en: "Branding page sections" })} className="border-b border-line bg-mist">
        <Container className="flex flex-wrap gap-x-8 gap-y-1 py-3">
          {[
            { id: "branding-scope", label: { ar: "الشعار والهوية", en: "Logo & identity" } },
            { id: "branding-packaging", label: { ar: "التغليف والتطبيقات", en: "Packaging & applications" } },
            { id: "branding-process", label: { ar: "مراحل العمل", en: "Our process" } },
            { id: "branding-delivery", label: { ar: "دليل الهوية والتسليم", en: "Guidelines & delivery" } },
          ].map((item) => <a key={item.id} href={`#${item.id}`} className="inline-flex min-h-11 items-center gap-3 text-sm font-medium text-ink hover:text-blue"><span className="size-1.5 rounded-full bg-blue" aria-hidden="true" />{t(item.label)}</a>)}
        </Container>
      </nav>

      <Section id="branding-scope" className="scroll-mt-28">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow={t({ ar: "هوية متكاملة", en: "A complete identity" })} title={t({ ar: "كل عنصر يحكي الفكرة نفسها.", en: "Every element tells the same story." })} />
          </div>
          <p className="text-lg leading-8 text-slate lg:col-span-5 lg:pt-10">{t({ ar: "الشعار هو البداية. نبني حوله نظامًا متكاملًا يربط شخصية شركتك بمظهرها، ويمنحك حضورًا متسقًا على الشاشة وفي العالم الحقيقي.", en: "The logo is the starting point. We build a complete system around it, connecting your business’s personality to its appearance, on screen and in the physical world." })}</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {brandingCapabilities.map((capability, index) => (
            <article key={capability.title.en} className="rounded-r16 border border-line bg-white p-7">
              <div className="flex items-center justify-between text-blue"><span className="text-sm font-medium" dir="ltr">0{index + 1}</span><Icon name={index === 0 ? "branding" : index === 1 ? "erp" : "presence"} /></div>
              <h3 className="mt-8 text-xl font-medium text-ink">{t(capability.title)}</h3>
              <p className="mt-4 text-base leading-7 text-slate">{t(capability.body)}</p>
              <div className="mt-6 border-t border-line pt-6"><CheckList columns={1} items={capability.items.map(t)} /></div>
            </article>
          ))}
        </div>
      </Section>

      <Section tone="mist" id="branding-packaging" className="scroll-mt-28">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <SectionHeading eyebrow={t({ ar: "تصميم العبوات والتغليف", en: "Packaging design & layout" })} title={t({ ar: "حضور يُرى. وتجربة تُلمس.", en: "Seen on the shelf. Felt in the hand." })} lead={t({ ar: "نصمم العبوة كجزء من تجربة العلامة؛ بتنظيم واضح للمعلومات، ومساحة مدروسة للشعار، وتكوين متسق عبر مجموعة منتجاتك.", en: "We design packaging as part of the brand experience, with clear information, considered logo placement and a consistent layout across your product range." })} />
            <div className="mt-8"><CheckList columns={1} items={[
              t({ ar: "توزيع التصميم على مخطط القص والطي المعتمد", en: "Artwork layout on the approved packaging dieline" }),
              t({ ar: "تصميم العبوات والملصقات وتنويعات المنتجات", en: "Packs, labels and product variants" }),
              t({ ar: "نماذج عرض تصوّر الشكل قبل الإنتاج", en: "Mockups to review the design before production" }),
              t({ ar: "تجهيز ملفات التصميم وفق مواصفات المورد", en: "Design files prepared to supplier specifications" }),
            ]} /></div>
            <p className="mt-7 text-sm leading-6 text-slate">{t({ ar: "نحدد التطبيقات المطلوبة منذ البداية؛ سواء لمنتج واحد أو مجموعة منتجات.", en: "We agree the applications at the start, whether for one product or a complete range." })}</p>
          </div>
          <PackagingLayout />
        </div>
      </Section>

      <Section id="branding-process" className="scroll-mt-28">
        <div className="grid items-start gap-12 lg:grid-cols-[0.85fr_1.5fr] lg:gap-20">
          <div className="lg:sticky lg:top-32">
            <SectionHeading eyebrow={t({ ar: "من الفكرة إلى التسليم", en: "From discovery to delivery" })} title={t({ ar: "خمس مراحل. اتجاه واحد واضح.", en: "Five stages. One clear direction." })} lead={t({ ar: "قرارات مبنية على فهم، ومخرجات واضحة في كل خطوة. نراجع كل مرحلة معك قبل أن نبني عليها.", en: "Informed decisions and clear deliverables at every step. We review each stage with you before building on it." })} />
            <div className="mt-8 inline-flex items-center gap-3 rounded-r8 bg-ice px-4 py-3 text-sm font-medium text-blue"><Icon name="check" className="size-5 shrink-0" />{t({ ar: "مراجعة واعتماد في كل مرحلة", en: "A review checkpoint at every stage" })}</div>
          </div>
          <ol className="min-w-0">
            {brandingStages.map((stage, index) => (
              <li key={stage.title.en} className={`relative grid grid-cols-[3rem_1fr] gap-5 pb-10 last:pb-0 sm:grid-cols-[3.5rem_1fr] sm:gap-7 ${styles.stage}`}>
                <span className="relative z-10 flex size-12 items-center justify-center rounded-r16 bg-blue text-base font-medium text-white sm:size-14" dir="ltr">0{index + 1}</span>
                <div className="min-w-0 border-b border-line pb-10">
                  <h3 className="text-xl font-medium text-ink sm:text-2xl">{t(stage.title)}</h3>
                  <p className="mt-3 text-base leading-7 text-slate">{t(stage.body)}</p>
                  <p className="mt-4 text-sm font-medium text-blue">{t({ ar: "المخرج: ", en: "Deliverable: " })}{t(stage.deliverable)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Section>

      <Section tone="mist" id="branding-delivery" className="scroll-mt-28">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <SectionHeading eyebrow={t({ ar: "جاهزة للاستخدام والنمو", en: "Ready to use. Ready to grow." })} title={t({ ar: "نسلّمك الهوية، ومفاتيح استخدامها.", en: "Your identity, and the tools to use it." })} lead={t({ ar: "دليل واضح وملفات منظمة تجعل فريقك وشركاءك قادرين على تطبيق الهوية بثبات، وتوسيعها مع نمو أعمالك.", en: "Clear guidelines and organized assets help your team and partners apply the identity consistently and extend it as your business grows." })} />
          <div className="rounded-r24 border border-line bg-white p-7 sm:p-10">
            <p className="mb-6 text-sm font-medium text-blue">{t({ ar: "حزمة التسليم المتفق عليها", en: "Your agreed delivery package" })}</p>
            <CheckList columns={1} items={[
              t({ ar: "دليل الشعار والألوان والخطوط والتطبيقات", en: "Logo, color, typography and application guidelines" }),
              t({ ar: "ملفات الشعار المتجهة وصيغ الاستخدام المختلفة", en: "Vector logos and exports for everyday use" }),
              t({ ar: "ملفات التطبيقات والقوالب المتفق عليها", en: "Agreed application files and templates" }),
              t({ ar: "توضيح تراخيص الموارد وجلسة تسليم للفريق", en: "Resource licensing notes and a team handover" }),
            ]} />
          </div>
        </div>
      </Section>

      <SelectedBrandingWork />

      <Section>
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHeading eyebrow={t({ ar: "قبل أن نبدأ", en: "Before we begin" })} title={t({ ar: "أسئلة عن بناء هويتك.", en: "Questions about your brand." })} /></div>
          <div className="lg:col-span-8"><FAQList items={brandingService.faqs.map((faq) => ({ q: t(faq.q), a: t(faq.a) }))} /></div>
        </div>
      </Section>

      <section className="bg-white pb-20 md:pb-28">
        <Container>
          <div className="grid gap-10 rounded-r24 bg-blue px-8 py-12 text-white md:px-12 md:py-16 lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="max-w-2xl">
              <Eyebrow light>{t({ ar: "الخطوة الأولى: نتعرّف عليك", en: "First, let’s get to know you" })}</Eyebrow>
              <h2 className="brand-h2 mt-5 font-medium">{t({ ar: "لنصمم الصورة التي تستحقها شركتك.", en: "Let’s shape how the world sees your business." })}</h2>
              <p className="mt-5 text-lg leading-8">{t({ ar: "أخبرنا عن نشاطك ومرحلتك الحالية. نحدد معك ما تحتاجه علامتك، ونقترح نطاقًا واضحًا للمشروع.", en: "Tell us about your business and where you are today. Together, we’ll define what your brand needs and a clear project scope." })}</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:flex-col">
              <Button to={projectPath} variant="inverse">{t({ ar: "ناقش مشروع هويتك", en: "Discuss your brand project" })}</Button>
              <Link to="/contact?service=branding" className="inline-flex min-h-12 items-center justify-center rounded-r8 px-5 text-base font-medium text-white ring-1 ring-white hover:bg-white/10">{t({ ar: "تواصل مع الفريق", en: "Talk to our team" })}</Link>
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}
